import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_ATTENDANCE_SETTINGS,
  type AttendanceSettings,
  type UpdateAttendanceSettingsInput,
} from '@tailoring-crm/shared';
import { AttendanceKioskLinkCard } from '../components/attendance/attendance-kiosk-link-card.js';
import { PageHeader } from '../components/layout/page-header.js';
import { Button, Input, Label, useToast } from '@tailoring-crm/ui';
import { isApiRequestError } from '../lib/api/errors.js';
import {
  useAttendanceSettings,
  useUpdateAttendanceSettings,
} from '../lib/attendance/use-attendance.js';
import { useAuth } from '../lib/auth/auth-context.js';

/**
 * The shop's workday, and how much lateness is lateness.
 *
 * These five numbers are the only thing that gives the `late` and `halfDay` flags a meaning, and
 * shops disagree about them by a lot — a boutique that opens at ten does not consider five past
 * ten late. So the form seeds from the shared defaults while the query is in flight rather than
 * from nothing: an owner who opens this screen sees the settings their shop is ACTUALLY being
 * judged by from the first paint, not empty boxes that would read as "nothing is set".
 *
 * IT SENDS ONLY WHAT CHANGED. The contract takes a partial patch, and sending the whole object
 * back would have this screen quietly re-assert four settings somebody else may have edited in
 * the meantime.
 *
 * A workday that ends before it starts is refused by the server — an overnight shift has no
 * honest row in a module that counts one calendar day per person — and that refusal is printed
 * beside the field rather than swallowed, because a Save button that does nothing is the worst
 * possible answer to "why did it not save?".
 */
export function AttendanceSettingsPage(): ReactElement {
  const { t } = useTranslation();
  const { role } = useAuth();
  const { toast } = useToast();

  const isTailor = role === 'tailor';
  // Not asked for at all by somebody the API would refuse.
  const settings = useAttendanceSettings({ enabled: !isTailor });
  const save = useUpdateAttendanceSettings();

  /** What has been typed. Null until somebody touches a field, so the query stays in charge. */
  const [draft, setDraft] = useState<AttendanceSettings | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  if (isTailor) {
    return (
      <>
        <PageHeader title={t('attendance.settings.title')} />
        <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          {t('attendance.settings.refused')}
        </p>
      </>
    );
  }

  /** What the shop currently has, or the shared defaults while that is still on its way. */
  const saved: AttendanceSettings = settings.data ?? DEFAULT_ATTENDANCE_SETTINGS;
  const value: AttendanceSettings = draft ?? saved;

  const set = <TKey extends keyof AttendanceSettings>(
    key: TKey,
    next: AttendanceSettings[TKey],
  ): void => {
    setRefusal(null);
    setDraft({ ...value, [key]: next });
  };

  /** Only the keys that differ from what the shop already has. */
  const patch: UpdateAttendanceSettingsInput = {
    ...(value.workdayStart !== saved.workdayStart ? { workdayStart: value.workdayStart } : {}),
    ...(value.workdayEnd !== saved.workdayEnd ? { workdayEnd: value.workdayEnd } : {}),
    ...(value.graceMinutes !== saved.graceMinutes ? { graceMinutes: value.graceMinutes } : {}),
    ...(value.halfDayThresholdMinutes !== saved.halfDayThresholdMinutes
      ? { halfDayThresholdMinutes: value.halfDayThresholdMinutes }
      : {}),
    ...(value.selfCheckInEnabled !== saved.selfCheckInEnabled
      ? { selfCheckInEnabled: value.selfCheckInEnabled }
      : {}),
  };
  const changed = Object.keys(patch).length > 0;

  const submit = (): void => {
    if (!changed) return;
    void (async () => {
      try {
        await save.mutateAsync(patch);
        setDraft(null);
        setRefusal(null);
        toast({ title: t('attendance.settings.saved'), variant: 'success' });
      } catch (error) {
        // The server's own words, beside the form. A silent no-op would be unreadable.
        setRefusal(refusalMessage(error, t('error.unexpected')));
      }
    })();
  };

  return (
    <>
      <PageHeader
        title={t('attendance.settings.title')}
        description={t('attendance.settings.description')}
      />

      <form
        className="flex max-w-xl flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor="attendance-workday-start">
              {t('attendance.settings.workdayStart')}
            </Label>
            <Input
              id="attendance-workday-start"
              type="time"
              value={value.workdayStart}
              onChange={(event) => set('workdayStart', event.target.value)}
            />
          </div>
          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor="attendance-workday-end">{t('attendance.settings.workdayEnd')}</Label>
            <Input
              id="attendance-workday-end"
              type="time"
              value={value.workdayEnd}
              onChange={(event) => set('workdayEnd', event.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="attendance-grace-minutes">{t('attendance.settings.graceMinutes')}</Label>
          <Input
            id="attendance-grace-minutes"
            type="number"
            inputMode="numeric"
            min={0}
            max={180}
            value={String(value.graceMinutes)}
            onChange={(event) => set('graceMinutes', Number(event.target.value))}
          />
          <p className="text-sm text-muted-foreground">{t('attendance.settings.graceHint')}</p>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="attendance-half-day-minutes">
            {t('attendance.settings.halfDayThresholdMinutes')}
          </Label>
          <Input
            id="attendance-half-day-minutes"
            type="number"
            inputMode="numeric"
            min={30}
            max={720}
            value={String(value.halfDayThresholdMinutes)}
            onChange={(event) => set('halfDayThresholdMinutes', Number(event.target.value))}
          />
          <p className="text-sm text-muted-foreground">{t('attendance.settings.halfDayHint')}</p>
        </div>

        <div className="flex flex-col gap-2">
          <label
            htmlFor="attendance-self-check-in"
            className="flex items-center gap-2 text-sm font-medium"
          >
            <input
              id="attendance-self-check-in"
              type="checkbox"
              checked={value.selfCheckInEnabled}
              onChange={(event) => set('selfCheckInEnabled', event.target.checked)}
              className="size-4 rounded border-input"
            />
            {t('attendance.settings.selfCheckInEnabled')}
          </label>
          <p className="text-sm text-muted-foreground">
            {t('attendance.settings.selfCheckInHint')}
          </p>
        </div>

        {refusal ? (
          <p role="alert" className="text-sm text-destructive">
            {refusal}
          </p>
        ) : null}

        <div>
          <Button type="submit" disabled={!changed || save.isPending}>
            {save.isPending ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      </form>

      <div className="max-w-xl">
        <AttendanceKioskLinkCard />
      </div>
    </>
  );
}

/**
 * What to print beside the form.
 *
 * A validation refusal names the field it is about — "the workday must end after it starts" is
 * filed under `workdayEnd` — and those sentences are more useful than the envelope's summary, so
 * they are preferred when the server sent any.
 */
function refusalMessage(error: unknown, fallback: string): string {
  if (isApiRequestError(error)) {
    const fieldMessages = Object.values(error.fieldErrors ?? {}).flat();
    return fieldMessages.length > 0 ? fieldMessages.join(' ') : error.message;
  }
  return error instanceof Error ? error.message : fallback;
}

export default AttendanceSettingsPage;
